import { useMemo, useState, useEffect } from 'react';
import type { CSSProperties } from 'react';
import { Tag, Hash, Pencil, Trash2, Archive } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn, navActiveClass } from '@/lib/utils';
import { useNotesStore } from '@/stores/notesStore';
import { useNotesActionsStore, updateTag, deleteTag } from '@/stores/notesActionsStore';
import type { NoteTagFull } from '@/types/note';

export function NotesSidebarContent() {
  const tagsById = useNotesStore((state) => state.tags);
  const notesById = useNotesStore((state) => state.notes);
  const noteOrder = useNotesStore((state) => state.noteOrder);
  const selectedTagId = useNotesActionsStore((state) => state.selectedTagId);
  const setSelectedTagId = useNotesActionsStore((state) => state.setSelectedTagId);
  const selectedView = useNotesActionsStore((state) => state.selectedView);
  const setSelectedView = useNotesActionsStore((state) => state.setSelectedView);

  // Tag edit dialog state
  const [editingTag, setEditingTag] = useState<NoteTagFull | null>(null);

  // Derive arrays with useMemo to avoid creating new references on each render
  const tags = useMemo(
    () => Object.values(tagsById).sort((a, b) => a.name.localeCompare(b.name)),
    [tagsById]
  );

  const notes = useMemo(
    () =>
      noteOrder
        .map((id) => notesById[id])
        .filter((note) => note && note.closedAt && !note.archivedAt && !note.deletedAt),
    [noteOrder, notesById]
  );

  // Count notes per tag (loaded active notes only)
  const tagCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    notes.forEach((note) => {
      note.tags.forEach((tag) => {
        counts[tag.id] = (counts[tag.id] || 0) + 1;
      });
    });
    return counts;
  }, [notes]);

  const handleTagClick = (tagId: string | null) => {
    setSelectedView('active');
    setSelectedTagId(tagId === selectedTagId ? null : tagId);
  };

  const handleEditTag = (tag: NoteTagFull) => {
    setEditingTag(tag);
  };

  const handleDeleteTag = async (tagId: string) => {
    // If we're deleting the currently selected tag, clear selection
    if (selectedTagId === tagId) {
      setSelectedTagId(null);
    }
    try {
      await deleteTag(tagId);
    } catch (error) {
      console.error('Failed to delete tag:', error);
    }
  };

  return (
    <>
      <div className="p-2 pt-2 md:pt-4 space-y-5">
        <div className="space-y-1">
          <div className="px-3 py-2 text-xs font-semibold text-sidebar-foreground/70 flex items-center gap-2">
            <Tag className="h-3.5 w-3.5" />
            TAGS
          </div>

          <Button
            variant={selectedView === 'active' && selectedTagId === null ? 'secondary' : 'ghost'}
            className={cn(
              'w-full justify-start gap-3 font-normal',
              selectedView === 'active' && selectedTagId === null && navActiveClass,
              'max-md:select-none'
            )}
            onClick={() => {
              setSelectedView('active');
              setSelectedTagId(null);
            }}
            style={{ WebkitTouchCallout: 'none' } as React.CSSProperties}
          >
            <Hash className="h-4 w-4" />
            <span className="flex-1 text-left">All Notes</span>
            <span className="text-xs text-muted-foreground">{notes.length}</span>
          </Button>

          <Button
            variant={selectedView === 'archive' ? 'secondary' : 'ghost'}
            className={cn(
              'w-full justify-start gap-3 font-normal',
              selectedView === 'archive' && navActiveClass,
              'max-md:select-none'
            )}
            onClick={() => {
              setSelectedView(selectedView === 'archive' ? 'active' : 'archive');
              setSelectedTagId(null);
            }}
            style={{ WebkitTouchCallout: 'none' } as React.CSSProperties}
          >
            <Archive className="h-4 w-4" />
            <span className="flex-1 text-left">Archive</span>
          </Button>

          {tags.map((tag) => (
            <ContextMenu key={tag.id}>
              <ContextMenuTrigger asChild>
                <Button
                  variant={selectedTagId === tag.id ? 'secondary' : 'ghost'}
                  className={cn(
                    'w-full justify-start gap-3 font-normal',
                    selectedTagId === tag.id && navActiveClass,
                    'max-md:select-none'
                  )}
                  onClick={() => handleTagClick(tag.id)}
                  style={{ WebkitTouchCallout: 'none' } as CSSProperties}
                >
                  <span
                    className="h-3 w-3 rounded-full shrink-0"
                    style={{ backgroundColor: tag.color || 'var(--muted-foreground)' }}
                  />
                  <span className="flex-1 text-left truncate">{tag.name}</span>
                  {tagCounts[tag.id] && (
                    <span className="text-xs text-muted-foreground">{tagCounts[tag.id]}</span>
                  )}
                </Button>
              </ContextMenuTrigger>
              <ContextMenuContent className="w-48">
                <ContextMenuItem onSelect={() => handleEditTag(tag)}>
                  <Pencil className="h-4 w-4" />
                  Edit tag
                </ContextMenuItem>
                <ContextMenuSeparator />
                <ContextMenuItem variant="destructive" onSelect={() => handleDeleteTag(tag.id)}>
                  <Trash2 className="h-4 w-4" />
                  Delete tag
                </ContextMenuItem>
              </ContextMenuContent>
            </ContextMenu>
          ))}

          {tags.length === 0 && (
            <div className="px-3 py-4 text-sm text-muted-foreground text-center">
              No tags yet
            </div>
          )}
        </div>
      </div>

      {/* Tag Edit Dialog */}
      <TagEditDialog
        tag={editingTag}
        onClose={() => setEditingTag(null)}
      />
    </>
  );
}

interface TagEditDialogProps {
  tag: NoteTagFull | null;
  onClose: () => void;
}

function TagEditDialog({ tag, onClose }: TagEditDialogProps) {
  const [name, setName] = useState('');
  const [color, setColor] = useState('#f97316');

  useEffect(() => {
    if (tag) {
      setName(tag.name);
      setColor(tag.color ?? '#f97316');
    }
  }, [tag]);

  const handleSave = async () => {
    if (!tag || !name.trim()) return;
    try {
      await updateTag(tag.id, { name: name.trim(), color });
      onClose();
    } catch (error) {
      console.error('Failed to update tag:', error);
    }
  };

  return (
    <Dialog open={!!tag} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Tag</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Tag name"
            />
          </div>
          <div>
            <Label>Color</Label>
            <Input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="h-10"
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!name.trim()}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
